'use client';

import React, { useState, useCallback } from 'react';

// ─── GUIDED RECONSTRUCTION WORKFLOW ───────────────────────────────────────────
// PDF chapter selector, paste validation, auto-section-boundary detection,
// and completion tracker for the Open Letter reconstruction

const PDF_CHAPTERS = [
  { id: 'ch1', label: 'Chapter 1 — The Recognition', pages: '1–8', sections: [1, 2, 3], keywords: ['recognition', 'caretaker', 'mural', 'khalifah'] },
  { id: 'ch2', label: 'Chapter 2 — Roots Ma\'rifah', pages: '9–18', sections: [4, 5, 6, 7, 8], keywords: ['marifah', 'roots', 'al-qadir', 'aql', 'ilm', 'hawas'] },
  { id: 'ch3', label: 'Chapter 3 — Names & Attributes', pages: '19–28', sections: [9, 10, 11, 12, 13], keywords: ['al-alim', 'al-hakim', 'ar-rahman', 'jibril', 'islah'] },
  { id: 'ch4', label: 'Chapter 4 — The Platform', pages: '29–38', sections: [14, 15, 16, 17, 18], keywords: ['haseeb', 'raqib', 'till', 'rope', 'amanah', 'haqq'] },
  { id: 'ch5', label: 'Chapter 5 — Creation & Witness', pages: '39–48', sections: [19, 20, 21, 22, 23], keywords: ['khaliq', 'khabir', 'zahir', 'examination', 'steppin'] },
  { id: 'ch6', label: 'Chapter 6 — Deeper Roots', pages: '49–58', sections: [24, 25, 26, 27, 28], keywords: ['al-latif', 'al-basir', 'al-sami', 'al-wali', 'al-mujib'] },
  { id: 'ch7', label: 'Chapter 7 — The Caretaker\'s Path', pages: '59–68', sections: [29, 30, 31, 32, 33], keywords: ['fitrah', 'mizan', 'tawbah', 'sabr', 'shukr'] },
  { id: 'ch8', label: 'Chapter 8 — Closing & Call', pages: '69–78', sections: [34, 35, 36, 37, 38, 39, 40, 41], keywords: ['closing', 'call', 'action', 'fisabilillah', 'khidmah'] },
];

const SECTION_TITLES: Record<number, string> = {
  1: 'The Recognition', 2: 'Caretaker', 3: 'The Mural', 4: "Roots — Ma'rifah",
  5: 'Al-Qadir', 6: 'Aql — Intellect', 7: 'Ilm — Knowledge', 8: 'Hawas — The Senses',
  9: 'Al-Alim · Al-Hakim', 10: 'Entering the Platform', 11: 'Islah — Reform',
  12: 'Ar-Rahman — Compassion', 13: 'Hadith of Jibril', 14: 'Al-Haseeb · Ar-Raqib',
  15: 'Till Rows', 16: 'Rope of Language', 17: 'Amanah — Trust', 18: 'Haqq — Truth',
  19: 'Al-Khaliq — The Creator', 20: 'Al-Khabir · Al-Zahir', 21: 'The Examination',
  22: 'Steppin', 23: 'Closing', 24: 'Al-Latif — The Subtle', 25: 'Al-Basir — The All-Seeing',
  26: 'Al-Sami — The All-Hearing', 27: 'Al-Wali — The Protecting Friend', 28: 'Al-Mujib — The Responsive',
  29: 'Fitrah — Original Design', 30: 'Mizan — The Balance', 31: 'Tawbah — Return',
  32: 'Sabr — Patient Perseverance', 33: 'Shukr — Gratitude', 34: 'The Caretaker\'s Covenant',
  35: 'Fisabilillah — In the Path of Allah', 36: 'Khidmah — Service', 37: 'Uboor — Crossing',
  38: 'The Garden', 39: 'The Mural Revisited', 40: 'The Letter Closes', 41: 'Bismillah — The Opening',
};

// ── Paste Validation ──────────────────────────────────────────────────────────
interface ValidationResult {
  valid: boolean;
  wordCount: number;
  issues: string[];
  suggestions: string[];
  detectedSection: number | null;
  confidence: number;
}

function validatePaste(text: string, expectedSection: number): ValidationResult {
  const issues: string[] = [];
  const suggestions: string[] = [];
  const wordCount = text.trim().split(/\s+/).filter(Boolean).length;

  // Check minimum length
  if (wordCount < 20) issues.push('Text appears too short — minimum 20 words expected for a section.');
  if (wordCount < 50) suggestions.push('Consider pasting the full section text for better reconstruction fidelity.');

  // Check for common PDF artifacts
  if (/\d{1,3}\n/.test(text)) suggestions.push('Detected possible page numbers — consider removing them before pasting.');
  if (/[^\x00-\x7F]/.test(text) && !/[\u0600-\u06FF]/.test(text)) suggestions.push('Non-standard characters detected — verify Arabic text is preserved correctly.');

  // Check for formatting issues
  if (text.includes('  ') && text.split('  ').length > 3) suggestions.push('Multiple double-spaces detected — may indicate PDF column artifacts.');
  if (/\n{3,}/.test(text)) suggestions.push('Multiple blank lines detected — may indicate section breaks within the pasted content.');

  // Auto-detect section boundary
  const sectionTitle = SECTION_TITLES[expectedSection]?.toLowerCase() || '';
  const lowerText = text.toLowerCase();
  let detectedSection: number | null = null;
  let confidence = 0;

  // Check if text contains keywords from expected section
  const sectionKeywords = sectionTitle.split(/[\s·—]+/).filter(w => w.length > 3);
  const matchCount = sectionKeywords.filter(kw => lowerText.includes(kw)).length;
  if (matchCount > 0) {
    detectedSection = expectedSection;
    confidence = Math.min(100, Math.round((matchCount / Math.max(sectionKeywords.length, 1)) * 100));
  }

  // Check for Arabic text (good sign for letter content)
  if (/[\u0600-\u06FF]/.test(text)) {
    confidence = Math.min(100, confidence + 15);
    suggestions.push('Arabic text detected — ensure it is properly oriented (right-to-left).');
  }

  const valid = issues.length === 0 && wordCount >= 20;

  return { valid, wordCount, issues, suggestions, detectedSection, confidence };
}

// ── Section Completion Tracker ────────────────────────────────────────────────
function CompletionTracker({ completedSections, totalSections }: { completedSections: Set<number>; totalSections: number }) {
  const pct = Math.round((completedSections.size / totalSections) * 100);
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: 8, padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '.12em', textTransform: 'uppercase' }}>Reconstruction Progress</p>
        <p style={{ fontFamily: 'monospace', fontSize: 14, color: '#c9a84c', fontWeight: 700 }}>{pct}%</p>
      </div>
      <div style={{ height: 4, background: 'rgba(201,168,76,0.1)', borderRadius: 2, overflow: 'hidden', marginBottom: 10 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#2d7a47' : 'rgba(201,168,76,0.6)', borderRadius: 2, transition: 'width 0.4s ease' }} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {Array.from({ length: totalSections }, (_, i) => i + 1).map(n => (
          <div
            key={n}
            title={`Section ${n}: ${SECTION_TITLES[n] || ''}`}
            style={{ width: 16, height: 16, borderRadius: 2, background: completedSections.has(n) ? '#2d7a47' : 'rgba(201,168,76,0.08)', border: `1px solid ${completedSections.has(n) ? 'rgba(45,122,71,0.5)' : 'rgba(201,168,76,0.12)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 6, color: completedSections.has(n) ? '#7EC8A0' : 'rgba(201,168,76,0.2)', fontFamily: 'monospace' }}
          >
            {n}
          </div>
        ))}
      </div>
      <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', marginTop: 8 }}>
        {completedSections.size} of {totalSections} sections filled · {totalSections - completedSections.size} remaining
      </p>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function GuidedReconstructionWorkflow() {
  const [selectedChapter, setSelectedChapter] = useState<string>('ch1');
  const [selectedSection, setSelectedSection] = useState<number>(1);
  const [pasteText, setPasteText] = useState('');
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [completedSections, setCompletedSections] = useState<Set<number>>(new Set());
  const [showInstructions, setShowInstructions] = useState(true);

  const chapter = PDF_CHAPTERS.find(c => c.id === selectedChapter) || PDF_CHAPTERS[0];

  const handlePaste = useCallback((text: string) => {
    setPasteText(text);
    if (text.trim().length > 10) {
      const result = validatePaste(text, selectedSection);
      setValidation(result);
    } else {
      setValidation(null);
    }
  }, [selectedSection]);

  const handleConfirm = () => {
    if (pasteText.trim().length > 20) {
      setCompletedSections(prev => new Set([...prev, selectedSection]));
      setPasteText('');
      setValidation(null);
      // Auto-advance to next section
      const nextSection = selectedSection + 1;
      if (nextSection <= 41) {
        setSelectedSection(nextSection);
        // Auto-select chapter for next section
        const nextChapter = PDF_CHAPTERS.find(c => c.sections.includes(nextSection));
        if (nextChapter) setSelectedChapter(nextChapter.id);
      }
    }
  };

  const C = {
    bg: '#060d12',
    gold: '#c9a84c',
    goldText: 'rgba(201,168,76,0.6)',
    cream: '#F5F0E8',
    creamFaint: 'rgba(212,197,169,0.5)',
  };

  return (
    <section style={{ background: C.bg, borderTop: '1px solid rgba(201,168,76,0.08)', padding: '60px 0' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 24px' }}>
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: C.goldText, marginBottom: 8 }}>
            ◈ GUIDED RECONSTRUCTION WORKFLOW · SECTION-BY-SECTION
          </p>
          <h2 style={{ fontFamily: 'monospace', fontSize: 'clamp(14px, 3vw, 20px)', color: C.cream, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Open Letter Guided Reconstruction
          </h2>
          <p style={{ color: C.creamFaint, fontSize: 12, lineHeight: 1.7, maxWidth: 600 }}>
            Select a PDF chapter, choose a section, paste the content, and validate before confirming. Auto-boundary detection identifies section markers. Completion tracker shows progress across all 41 sections.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* PDF Chapter Selector */}
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: 8, padding: '14px 16px' }}>
              <p style={{ fontFamily: 'monospace', fontSize: 8, color: C.goldText, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 10 }}>PDF Chapter Selector</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {PDF_CHAPTERS.map(ch => (
                  <button
                    key={ch.id}
                    onClick={() => { setSelectedChapter(ch.id); setSelectedSection(ch.sections[0]); }}
                    style={{ padding: '8px 10px', background: selectedChapter === ch.id ? 'rgba(201,168,76,0.08)' : 'transparent', border: `1px solid ${selectedChapter === ch.id ? 'rgba(201,168,76,0.3)' : 'rgba(201,168,76,0.06)'}`, borderRadius: 4, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <span style={{ fontFamily: 'monospace', fontSize: 8, color: selectedChapter === ch.id ? C.gold : C.creamFaint, letterSpacing: '.04em' }}>{ch.label}</span>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(212,197,169,0.25)' }}>pp. {ch.pages}</span>
                      <span style={{ fontFamily: 'monospace', fontSize: 6, color: ch.sections.every(s => completedSections.has(s)) ? '#2d7a47' : 'rgba(201,168,76,0.25)', background: ch.sections.every(s => completedSections.has(s)) ? 'rgba(45,122,71,0.12)' : 'transparent', padding: '1px 4px', borderRadius: 2 }}>
                        {ch.sections.filter(s => completedSections.has(s)).length}/{ch.sections.length}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Section selector for current chapter */}
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: 8, padding: '14px 16px' }}>
              <p style={{ fontFamily: 'monospace', fontSize: 8, color: C.goldText, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 10 }}>
                Sections · {chapter.label}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {chapter.sections.map(n => (
                  <button
                    key={n}
                    onClick={() => setSelectedSection(n)}
                    style={{ padding: '7px 10px', background: selectedSection === n ? 'rgba(201,168,76,0.08)' : 'transparent', border: `1px solid ${selectedSection === n ? 'rgba(201,168,76,0.3)' : 'rgba(201,168,76,0.06)'}`, borderRadius: 4, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <div style={{ width: 18, height: 18, border: `1px solid ${completedSections.has(n) ? 'rgba(45,122,71,0.5)' : 'rgba(201,168,76,0.2)'}`, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', background: completedSections.has(n) ? 'rgba(45,122,71,0.15)' : 'transparent', flexShrink: 0 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 7, color: completedSections.has(n) ? '#2d7a47' : 'rgba(201,168,76,0.3)' }}>{completedSections.has(n) ? '✓' : n}</span>
                    </div>
                    <span style={{ fontFamily: 'monospace', fontSize: 8, color: selectedSection === n ? C.gold : completedSections.has(n) ? '#2d7a47' : C.creamFaint, letterSpacing: '.04em' }}>
                      {SECTION_TITLES[n] || `Section ${n}`}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Completion tracker */}
            <CompletionTracker completedSections={completedSections} totalSections={41} />
          </div>

          {/* Right column — paste zone */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Current section info */}
            <div style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 8, padding: '12px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, border: '1px solid rgba(201,168,76,0.3)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace', fontSize: 10, color: C.gold, background: 'rgba(201,168,76,0.06)' }}>
                  {selectedSection}
                </div>
                <div>
                  <p style={{ fontFamily: 'monospace', fontSize: 11, color: C.cream, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                    {SECTION_TITLES[selectedSection] || `Section ${selectedSection}`}
                  </p>
                  <p style={{ fontFamily: 'monospace', fontSize: 7, color: C.goldText, letterSpacing: '.06em' }}>
                    {chapter.label} · pp. {chapter.pages}
                  </p>
                </div>
                {completedSections.has(selectedSection) && (
                  <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 7, color: '#2d7a47', background: 'rgba(45,122,71,0.12)', border: '1px solid rgba(45,122,71,0.3)', padding: '2px 7px', borderRadius: 10 }}>✓ Filled</span>
                )}
              </div>
            </div>

            {/* Instructions toggle */}
            <button
              onClick={() => setShowInstructions(s => !s)}
              style={{ padding: '6px 12px', background: 'transparent', border: '1px solid rgba(45,138,135,0.2)', borderRadius: 4, color: 'rgba(45,138,135,0.6)', fontFamily: 'monospace', fontSize: 8, cursor: 'pointer', textAlign: 'left', letterSpacing: '.06em' }}
            >
              {showInstructions ? '▲' : '▼'} Instructions
            </button>
            {showInstructions && (
              <div style={{ background: 'rgba(45,138,135,0.04)', border: '1px solid rgba(45,138,135,0.12)', borderRadius: 6, padding: '10px 14px' }}>
                <ol style={{ color: C.creamFaint, fontSize: 9, lineHeight: 1.8, paddingLeft: 14, margin: 0 }}>
                  <li>Open the PDF alongside this page</li>
                  <li>Select the chapter and section above</li>
                  <li>Find the corresponding section in the PDF</li>
                  <li>Copy the section text (Ctrl+C / Cmd+C)</li>
                  <li>Paste into the text area below (Ctrl+V / Cmd+V)</li>
                  <li>Review the validation results</li>
                  <li>Click "Confirm Section" to mark as complete</li>
                </ol>
              </div>
            )}

            {/* Paste zone */}
            <div style={{ border: `1px solid ${validation ? (validation.valid ? 'rgba(45,122,71,0.35)' : 'rgba(201,168,76,0.3)') : 'rgba(201,168,76,0.15)'}`, borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '8px 14px', background: 'rgba(201,168,76,0.03)', borderBottom: '1px solid rgba(201,168,76,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'monospace', fontSize: 8, color: C.goldText, letterSpacing: '.1em', textTransform: 'uppercase' }}>
                  Paste PDF Content · Section {selectedSection}
                </span>
                {pasteText && (
                  <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.35)' }}>
                    {pasteText.trim().split(/\s+/).filter(Boolean).length} words
                  </span>
                )}
              </div>
              <textarea
                value={pasteText}
                onChange={e => handlePaste(e.target.value)}
                placeholder={`Paste the PDF content for Section ${selectedSection}: ${SECTION_TITLES[selectedSection] || ''} here...\n\nCopy directly from the PDF and paste. The text will be validated before confirming.`}
                style={{ width: '100%', minHeight: 180, padding: '14px', background: '#060d12', border: 'none', color: '#d4c5a9', fontFamily: "'Georgia', serif", fontSize: 12, lineHeight: 1.8, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            {/* Validation results */}
            {validation && (
              <div style={{ background: validation.valid ? 'rgba(45,122,71,0.05)' : 'rgba(201,168,76,0.04)', border: `1px solid ${validation.valid ? 'rgba(45,122,71,0.2)' : 'rgba(201,168,76,0.15)'}`, borderRadius: 6, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 9, color: validation.valid ? '#2d7a47' : '#c9a84c', letterSpacing: '.08em', textTransform: 'uppercase' }}>
                    {validation.valid ? '✓ Validation Passed' : '◈ Validation Notes'}
                  </span>
                  <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.35)' }}>{validation.wordCount} words</span>
                  {validation.detectedSection && (
                    <span style={{ fontFamily: 'monospace', fontSize: 7, color: '#2d8a87', background: 'rgba(45,138,135,0.1)', padding: '1px 5px', borderRadius: 2 }}>
                      Section {validation.detectedSection} detected · {validation.confidence}% confidence
                    </span>
                  )}
                </div>
                {validation.issues.map((issue, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                    <span style={{ color: '#e05252', fontSize: 9, flexShrink: 0 }}>⚠</span>
                    <p style={{ color: 'rgba(224,82,82,0.7)', fontSize: 9, lineHeight: 1.5 }}>{issue}</p>
                  </div>
                ))}
                {validation.suggestions.map((sug, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                    <span style={{ color: '#c9a84c', fontSize: 9, flexShrink: 0 }}>◈</span>
                    <p style={{ color: 'rgba(201,168,76,0.6)', fontSize: 9, lineHeight: 1.5 }}>{sug}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Confirm button */}
            <button
              onClick={handleConfirm}
              disabled={!pasteText.trim() || (validation !== null && !validation.valid)}
              style={{ padding: '12px', background: pasteText.trim() ? 'rgba(45,122,71,0.15)' : 'rgba(255,255,255,0.02)', border: `1px solid ${pasteText.trim() ? 'rgba(45,122,71,0.4)' : 'rgba(201,168,76,0.1)'}`, borderRadius: 6, color: pasteText.trim() ? '#2d7a47' : 'rgba(212,197,169,0.2)', fontFamily: 'monospace', fontSize: 10, cursor: pasteText.trim() ? 'pointer' : 'not-allowed', letterSpacing: '.08em', textTransform: 'uppercase', transition: 'all 0.15s' }}
            >
              {completedSections.has(selectedSection) ? '↻ Update Section' : '✓ Confirm Section — Mark Complete'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
